import { useId } from 'react'
import { motion } from 'framer-motion'
import { AlertCircle, RefreshCw } from 'lucide-react'

interface ConfirmationDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  details?: string
  confirmText?: string
  cancelText?: string
  type?: 'danger' | 'warning' | 'info' | 'success'
  isLoading?: boolean
  icon?: React.ReactNode
  /** When true, the confirm button is disabled (e.g. until a typed phrase matches). */
  confirmDisabled?: boolean
  /** Optional label for a confirmation text field shown above the buttons. */
  confirmInputLabel?: string
  /** Controlled value for the confirmation input (used with `onConfirmInputChange`). */
  confirmInputValue?: string
  onConfirmInputChange?: (value: string) => void
  /** Placeholder for the confirmation input. */
  confirmInputPlaceholder?: string
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  details,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
  isLoading = false,
  icon,
  confirmDisabled = false,
  confirmInputLabel,
  confirmInputValue,
  onConfirmInputChange,
  confirmInputPlaceholder,
}) => {
  const confirmInputDomId = useId()
  if (!isOpen) return null

  const getIconColor = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-100 text-red-600'
      case 'warning':
        return 'bg-yellow-100 text-yellow-600'
      case 'success':
        return 'bg-green-100 text-green-600'
      case 'info':
        return 'bg-blue-100 text-blue-600'
      default:
        return 'bg-yellow-100 text-yellow-600'
    }
  }

  const getButtonColor = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-700'
      case 'warning':
        return 'bg-yellow-600 hover:bg-yellow-700'
      case 'success':
        return 'bg-green-600 hover:bg-green-700'
      case 'info':
        return 'bg-blue-600 hover:bg-blue-700'
      default:
        return 'bg-yellow-600 hover:bg-yellow-700'
    }
  }

  const defaultIcon = icon || (
    <AlertCircle className="w-6 h-6" />
  )

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ 
        position: 'fixed',
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        width: '100vw', 
        height: '100vh', 
        zIndex: 99999,
        margin: 0,
        padding: '16px',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl p-8 shadow-xl max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full ${getIconColor()}`}>
          {defaultIcon}
        </div>
        
        <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
          {title}
        </h2>
        
        <p className="text-gray-600 text-center mb-2">
          {message}
        </p>

        {details && (
          <div className="bg-gray-50 rounded-lg p-4 mt-4 mb-6">
            <p className="text-sm text-gray-700 whitespace-pre-line">
              {details}
            </p>
          </div>
        )}

        {confirmInputLabel != null && onConfirmInputChange && (
          <div className="mb-6">
            <label className="block text-xs font-medium text-gray-600 mb-1.5" htmlFor={confirmInputDomId}>
              {confirmInputLabel}
            </label>
            <input
              id={confirmInputDomId}
              type="text"
              autoComplete="off"
              value={confirmInputValue ?? ''}
              onChange={(e) => onConfirmInputChange(e.target.value)}
              placeholder={confirmInputPlaceholder}
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 disabled:opacity-50"
            />
          </div>
        )}
        
        <div className="flex space-x-3 mt-6">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading || confirmDisabled}
            className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${getButtonColor()}`}
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                Processing...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default ConfirmationDialog


















